// First-run 3-step tour overlay on Home. Renders AFTER both the
// TextSizeFirstRun and CaregiverKindFirstRun cards have been dismissed
// (so we don't stack three prompts at once on a fresh account). Persists
// dismissal server-side via me.accessibility.tourSeen so it doesn't
// re-appear when the user signs in on another device.
//
// Senior-friendly: large copy, single-tap dismissal, a generous Skip
// button, keyboard-accessible (Esc closes, arrow keys move between
// steps), respects prefers-reduced-motion.

import { useEffect, useState } from 'react';
import { useAuthedFetch } from '../hooks/useAuthedFetch';
import { useMe } from '../hooks/useMe';
import { SiggyAvatar } from './SiggyAvatar';

interface Step {
  title: string;
  body: string;
}

const STEPS: Step[] = [
  {
    title: 'Add your medicines',
    body: 'Snap a photo of any bottle. Or type the name. We will find safety information for each one.',
  },
  {
    title: 'Ask Siggy anything',
    body: 'Plain-English answers about your medicines. Any time. No appointment, no waiting room.',
  },
  {
    title: 'Bring a clean list to your doctor',
    body: 'Print a Doctor Visit summary, a Fridge Sheet, or a Wallet Card any time. Always current.',
  },
];

// Cookie that tracks how many separate Home visits the user has made.
// We use it to defer the tour overlay to visit 3+ so a brand-new
// user sees the dashboard alone on their first one or two visits
// before getting any guided-tour scaffolding. Earl (round-4):
// "you didn't calibrate, you doubled down." This is the calibration.
const HOME_VISIT_COOKIE = 'mr_home_visits';
function bumpHomeVisitCount(): number {
  if (typeof document === 'undefined') return 0;
  const match = document.cookie.match(new RegExp(`${HOME_VISIT_COOKIE}=(\\d+)`));
  const current = match ? Number(match[1] ?? '0') : 0;
  const next = current + 1;
  document.cookie = `${HOME_VISIT_COOKIE}=${next}; path=/; max-age=${60 * 60 * 24 * 30}; samesite=lax`;
  return next;
}

export function HomeTour(): JSX.Element | null {
  const authedFetch = useAuthedFetch();
  const { me } = useMe();
  const [step, setStep] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [visits, setVisits] = useState(0);

  useEffect(() => {
    setVisits(bumpHomeVisitCount());
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setDismissed(true);
      if (e.key === 'ArrowRight') setStep((s) => Math.min(s + 1, STEPS.length - 1));
      if (e.key === 'ArrowLeft') setStep((s) => Math.max(s - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!me) return null;
  if (dismissed) return null;
  if (me.accessibility?.tourSeen === true) return null;
  // Defer until earlier first-run prompts are done so we don't stack.
  if (me.accessibility?.textSizeChosen !== true) return null;
  if (me.accessibility?.profileKindChosen !== true) return null;
  // Round-4 calibration: hold the tour until the user has actually
  // landed on Home a few times. Lets a senior look around first.
  if (visits < 3) return null;

  const isLast = step >= STEPS.length - 1;
  const current = STEPS[step];

  const finish = async (): Promise<void> => {
    setDismissed(true);
    try {
      await authedFetch('/api/me/accessibility', {
        method: 'PATCH',
        body: JSON.stringify({ tourSeen: true }),
      });
    } catch {
      // best-effort; local dismissal already happened
    }
  };

  const next = (): void => {
    if (isLast) {
      void finish();
    } else {
      setStep((s) => s + 1);
    }
  };

  // Render a centered card over a dimmed full-screen backdrop. Backdrop
  // tap also dismisses (after confirming with the Skip button label).
  return (
    // biome-ignore lint/a11y/useSemanticElements: <dialog> doesn't accept fixed-inset overlay styling we need; aria role + aria-modal cover semantics
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="home-tour-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        style={{
          background: 'var(--color-surface)',
          borderRadius: 20,
          padding: 24,
          maxWidth: 460,
          width: '100%',
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.18)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <SiggyAvatar size={56} expression={isLast ? 'celebrating' : 'default'} />
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-secondary)' }}>
            Step {step + 1} of {STEPS.length}
          </span>
        </div>
        <h2 id="home-tour-title" style={{ margin: '0 0 8px', fontSize: 24, lineHeight: 1.2 }}>
          {current?.title}
        </h2>
        <p style={{ margin: '0 0 24px', fontSize: 18, lineHeight: 1.5 }}>{current?.body}</p>

        {/* Progress dots */}
        <div
          aria-hidden="true"
          style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 20 }}
        >
          {STEPS.map((_, i) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: stable count, no reordering
              key={i}
              style={{
                width: i === step ? 24 : 10,
                height: 10,
                borderRadius: 999,
                background: i === step ? 'var(--color-primary)' : 'var(--color-border)',
                transition: 'width 0.2s ease',
              }}
            />
          ))}
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
          <button
            type="button"
            onClick={() => void finish()}
            style={{
              minHeight: 56,
              padding: '0 20px',
              background: 'transparent',
              color: 'var(--color-text-secondary)',
              border: 0,
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              borderRadius: 12,
            }}
          >
            Skip tour
          </button>
          <button
            type="button"
            onClick={next}
            style={{
              minHeight: 56,
              padding: '0 28px',
              background: 'var(--color-primary)',
              color: '#fff',
              border: 0,
              fontSize: 18,
              fontWeight: 700,
              cursor: 'pointer',
              borderRadius: 12,
            }}
          >
            {isLast ? "Let's go" : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
